import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import PageHeader from "../../../components/PageHeader";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import { useAuth } from "../../../context/AuthContext";
import { canVerTodasLojasUser, hasFullAccessUser } from "../../../lib/screenAccess";
import {
  controleProducaoService,
  FuncionamentoDiaProducao,
  TipoDiaFuncionamento,
} from "../../../services/controleProducaoService";
import { lojaService } from "../../../services/lojaService";
import { Loja } from "../../../types/loja";

type DiaDraft = FuncionamentoDiaProducao;

const HORARIOS_PADRAO = {
  turnoDiaInicio: "05:00",
  turnoDiaTermino: "21:59",
  turnoNoiteInicio: "22:00",
  turnoNoiteTermino: "04:59",
};

const OPCOES_TIPO_DIA: { valor: TipoDiaFuncionamento; label: string }[] = [
  { valor: "fechado", label: "Não abrem" },
  { valor: "sobra", label: "Abre (só sobra)" },
  { valor: "producao", label: "Produz" },
];

function diaAberto(tipoDia: TipoDiaFuncionamento): boolean {
  return tipoDia === "sobra" || tipoDia === "producao";
}

type LojaOpt = { value: number; label: string };

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;

const ControleProducaoFuncionamentoPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = hasFullAccessUser(user);
  const podeVerTodasLojas = canVerTodasLojasUser(user);
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    user?.lojaId
  );
  const [dias, setDias] = useState<DiaDraft[]>([]);
  const [configuraProducaoEmBirigui, setConfiguraProducaoEmBirigui] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const lojaOptions = useMemo(
    () => (podeVerTodasLojas ? lojas : lojas.filter((l) => l.id === user?.lojaId)),
    [podeVerTodasLojas, lojas, user?.lojaId]
  );

  const lojaSelectOptions = useMemo<LojaOpt[]>(
    () => lojaOptions.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojaOptions]
  );

  const selectedLojaOption =
    lojaSelectOptions.find((o) => o.value === selectedLojaId) ?? null;

  const load = useCallback(async (lojaId?: number) => {
    if (!lojaId) return;
    setLoading(true);
    try {
      const data = await controleProducaoService.getFuncionamento(lojaId);
      setDias(data.dias);
      setConfiguraProducaoEmBirigui(data.configuraProducaoEmBirigui === true);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Erro ao carregar funcionamento.";
      toast.error(message);
      setDias([]);
      setConfiguraProducaoEmBirigui(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void lojaService
      .getLojas()
      .then(setLojas)
      .catch(() => setLojas([]));
  }, []);

  useEffect(() => {
    if (lojaOptions.length === 0) return;
    const exists = lojaOptions.some((l) => l.id === selectedLojaId);
    if (!exists) {
      setSelectedLojaId(lojaOptions[0]?.id ?? user?.lojaId);
    }
  }, [lojaOptions, selectedLojaId, user?.lojaId]);

  useEffect(() => {
    if (selectedLojaId) {
      void load(selectedLojaId);
    }
  }, [selectedLojaId, load]);

  const atualizarDia = (diaSemana: number, patch: Partial<DiaDraft>) => {
    setDias((prev) =>
      prev.map((d) => (d.diaSemana === diaSemana ? { ...d, ...patch } : d))
    );
  };

  const replicarHorarios = (origem: DiaDraft) => {
    setDias((prev) =>
      prev.map((d) =>
        diaAberto(d.tipoDia)
          ? {
              ...d,
              turnoDiaInicio: origem.turnoDiaInicio,
              turnoDiaTermino: origem.turnoDiaTermino,
              turnoNoiteInicio: origem.turnoNoiteInicio,
              turnoNoiteTermino: origem.turnoNoiteTermino,
            }
          : d
      )
    );
    toast.info("Horários replicados para os demais dias abertos.");
  };

  const salvar = async () => {
    if (!selectedLojaId) return;
    for (const dia of dias) {
      if (!diaAberto(dia.tipoDia)) continue;
      const campos = [
        dia.turnoDiaInicio,
        dia.turnoDiaTermino,
        dia.turnoNoiteInicio,
        dia.turnoNoiteTermino,
      ];
      if (campos.some((c) => !c?.trim())) {
        toast.warning(`Preencha os horários de ${dia.label}.`);
        return;
      }
    }
    setSaving(true);
    try {
      const data = await controleProducaoService.saveFuncionamento({
        lojaId: selectedLojaId,
        dias: dias.map((d) => ({
          diaSemana: d.diaSemana,
          tipoDia: d.tipoDia,
          produzEmBirigui: d.produzEmBirigui === true,
          turnoDiaInicio: diaAberto(d.tipoDia) ? d.turnoDiaInicio : undefined,
          turnoDiaTermino: diaAberto(d.tipoDia) ? d.turnoDiaTermino : undefined,
          turnoNoiteInicio: diaAberto(d.tipoDia) ? d.turnoNoiteInicio : undefined,
          turnoNoiteTermino: diaAberto(d.tipoDia)
            ? d.turnoNoiteTermino
            : undefined,
        })),
      });
      setDias(data.dias);
      setConfiguraProducaoEmBirigui(data.configuraProducaoEmBirigui === true);
      toast.success("Funcionamento salvo.");
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Erro ao salvar funcionamento.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-4 mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <PageHeader
          title="Funcionamento da Produção"
          description="Defina o tipo de cada dia: fechado, aberto só para sobra, ou dia de produção. A base semanal usa apenas os dias de produção."
        />
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <ReactSelect<LojaOpt, false>
            inputId="funcionamento-loja-select"
            value={selectedLojaOption}
            onChange={(option: SingleValue<LojaOpt>) =>
              setSelectedLojaId(option?.value)
            }
            options={lojaSelectOptions}
            isClearable={isAdmin}
            placeholder="Selecionar loja"
            className="w-full min-w-[220px] sm:w-60"
            styles={LOJA_SELECT_STYLES}
          />
          {isAdmin && dias.length > 0 && (
            <button
              type="button"
              onClick={() => void salvar()}
              disabled={saving || loading}
              className="h-[38px] w-full rounded-md bg-primary px-4 text-sm font-semibold text-white disabled:opacity-60 sm:w-auto"
            >
              {saving ? "Salvando..." : "Salvar"}
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th className="px-3 py-3 text-left font-semibold text-neutral-600">
                Dia
              </th>
              <th className="px-3 py-3 text-left font-semibold text-neutral-600">
                Tipo
              </th>
              <th className="px-3 py-3 text-left font-semibold text-neutral-600">
                Turno dia/Produção
              </th>
              <th className="px-3 py-3 text-left font-semibold text-neutral-600">
                Turno noite/Sobras
              </th>
              <th className="px-3 py-3 text-left font-semibold text-neutral-600">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {dias.map((dia) => {
              const aberto = diaAberto(dia.tipoDia);
              return (
                <tr key={dia.diaSemana} className="border-t border-neutral-200">
                  <td className="px-3 py-3 font-medium text-neutral-800">
                    {dia.label}
                  </td>
                  <td className="px-3 py-3">
                    <select
                      value={dia.tipoDia}
                      disabled={!isAdmin || saving}
                      onChange={(e) => {
                        const tipoDia = e.target.value as TipoDiaFuncionamento;
                        atualizarDia(dia.diaSemana, {
                          tipoDia,
                          ...(diaAberto(tipoDia) && !aberto
                            ? HORARIOS_PADRAO
                            : {}),
                        });
                      }}
                      className="min-w-[10rem] rounded border border-neutral-300 px-2 py-1 disabled:bg-neutral-100"
                    >
                      {OPCOES_TIPO_DIA.map((op) => (
                        <option key={op.valor} value={op.valor}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={dia.turnoDiaInicio}
                        disabled={!aberto || !isAdmin || saving}
                        onChange={(e) =>
                          atualizarDia(dia.diaSemana, {
                            turnoDiaInicio: e.target.value,
                          })
                        }
                        className="rounded border border-neutral-300 px-2 py-1 disabled:bg-neutral-100"
                      />
                      <span className="text-neutral-400">até</span>
                      <input
                        type="time"
                        value={dia.turnoDiaTermino}
                        disabled={!aberto || !isAdmin || saving}
                        onChange={(e) =>
                          atualizarDia(dia.diaSemana, {
                            turnoDiaTermino: e.target.value,
                          })
                        }
                        className="rounded border border-neutral-300 px-2 py-1 disabled:bg-neutral-100"
                      />
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={dia.turnoNoiteInicio}
                        disabled={!aberto || !isAdmin || saving}
                        onChange={(e) =>
                          atualizarDia(dia.diaSemana, {
                            turnoNoiteInicio: e.target.value,
                          })
                        }
                        className="rounded border border-neutral-300 px-2 py-1 disabled:bg-neutral-100"
                      />
                      <span className="text-neutral-400">até</span>
                      <input
                        type="time"
                        value={dia.turnoNoiteTermino}
                        disabled={!aberto || !isAdmin || saving}
                        onChange={(e) =>
                          atualizarDia(dia.diaSemana, {
                            turnoNoiteTermino: e.target.value,
                          })
                        }
                        className="rounded border border-neutral-300 px-2 py-1 disabled:bg-neutral-100"
                      />
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {aberto && isAdmin && (
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => replicarHorarios(dia)}
                        className="text-xs font-medium text-primary underline disabled:opacity-50"
                      >
                        Replicar horários
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-neutral-500">
        <strong>Não abrem:</strong> loja fechada — sem ficha e sem contabilização.{" "}
        <strong>Abre (só sobra):</strong> consome estoque e lança sobra à noite.{" "}
        <strong>Produz:</strong> coluna na base semanal, produção diurna e sobra à noite.
      </p>

      {configuraProducaoEmBirigui && dias.length > 0 && (
        <section className="mt-8 rounded-lg border border-neutral-200 bg-white p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-800">
            Produção em Birigui
          </h2>
          <p className="mt-1 text-sm text-neutral-600">
            Selecione os dias em que os itens desta loja aparecem na ficha
            diária de produção (DIA) de Birigui.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-4">
            {dias.map((dia) => (
              <label
                key={`birigui-${dia.diaSemana}`}
                className="flex items-center gap-2 text-sm text-neutral-700"
              >
                <input
                  type="checkbox"
                  checked={dia.produzEmBirigui === true}
                  disabled={!isAdmin || saving}
                  onChange={(e) =>
                    atualizarDia(dia.diaSemana, {
                      produzEmBirigui: e.target.checked,
                    })
                  }
                  className="h-4 w-4 rounded border-neutral-300 text-red-700 focus:ring-red-600"
                />
                {dia.label}
              </label>
            ))}
          </div>
        </section>
      )}

      {loading && (
        <p className="mt-3 text-sm text-neutral-500">Carregando...</p>
      )}
    </main>
  );
};

export default ControleProducaoFuncionamentoPage;
